import {
  FC,
  useRef,
  useState,
  useMemo,
  useLayoutEffect,
  useEffect,
} from 'react';
import { useSelector } from 'react-redux';
import debounce from 'debounce';
import Button from '@raydiant/raydial/components/Button';
import Text from '@raydiant/raydial/components/Text';
import { Expand, CompressWide } from '../../../../../components/Icons';
import PresentationLoader from '../../../../../components/PresentationLoader';
import useApplicationVersion from '../../../../../hooks/useApplicationVersion';
import usePresentationUploads from '../../../../../hooks/usePresentationUploads';
import usePlaylistPageContext from '../../../usePlaylistPageContext';
import { useTheme } from '../../../../../hooks';
import { selectLocalUploads } from '../../../../../selectors/fileUploads';
import { getPresentationFileStatus } from '../../../../../utilities/presentationUtils';
import stepAddContentIcon from '../../assets/step-add-content.svg';
import stepScheduleIcon from '../../assets/step-schedule.svg';
import stepPreviewIcon from '../../assets/step-preview.svg';
import previewContentIcon from '../../assets/preview-content.svg';

// Matches app-renderer error UI for consistency.
const ERROR_CONTAINER_PADDING = 100;
const ERROR_TITLE_FONT_SIZE = 72;
const ERROR_TITLE_LETTER_SPACING = -0.6;
const ERROR_TITLE_LINE_HEIGHT = 1.22222;
const ERROR_TITLE_MARGIN_BOTTOM = 50;
const ERROR_BODY_FONT_SIZE = 36;
const ERROR_BODY_LINE_HEIGHT = 1.36111;

// Default preview resolution (1080p)
const PREVIEW_WIDTH = 1920;
const PREVIEW_HEIGHT = 1080;

const STEPS = [
  {
    icon: stepAddContentIcon,
    title: 'Add presentations to your list',
    description:
      'Add your content to a playlist. Videos, photos, menus, and more',
  },
  {
    icon: stepScheduleIcon,
    title: 'Schedule playlists',
    description:
      'Optionally, add a schedule to your content or nested playlists',
  },
  {
    icon: stepPreviewIcon,
    title: 'See how your content looks',
    description: 'Preview and publish to your devices!',
  },
];

interface PlaylistPreviewV2Props {
  presentationId?: string | null;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  hasItems?: boolean;
}

const PlaylistPreviewV2: FC<PlaylistPreviewV2Props> = ({
  presentationId,
  isExpanded = false,
  onToggleExpand,
  hasItems = false,
}) => {
  const page = usePlaylistPageContext();
  const containerRef = useRef<HTMLDivElement>(null);

  const presentation = presentationId
    ? page.getPresentation(presentationId)
    : undefined;

  const localUploads = useSelector(selectLocalUploads);
  const uploadStatus = presentation
    ? getPresentationFileStatus(presentation, localUploads)
    : { hasError: false };

  const presentationUploads = usePresentationUploads(presentation);

  // Queries
  const { data: appVersion } = useApplicationVersion(
    presentation?.applicationId,
    presentation?.appVersionId,
  );
  const { data: theme } = useTheme(presentation?.themeId);

  // Container dimensions for scaling
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  const calculateDimensions = () => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const w = Math.ceil(rect.width);
    const h = Math.ceil(rect.height);
    setDimensions((prev) =>
      prev.width === w && prev.height === h ? prev : { width: w, height: h },
    );
  };

  const debouncedCalculate = useMemo(
    () => debounce(calculateDimensions, 100),
    [],
  );

  useLayoutEffect(calculateDimensions);

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver(debouncedCalculate);
    observer.observe(containerRef.current);
    return () => {
      observer.disconnect();
      debouncedCalculate.clear();
    };
  }, [debouncedCalculate]);

  useEffect(() => {
    window.addEventListener('resize', debouncedCalculate);
    window.addEventListener('orientationchange', debouncedCalculate);
    return () => {
      window.removeEventListener('resize', debouncedCalculate);
      window.removeEventListener('orientationchange', debouncedCalculate);
      debouncedCalculate.clear();
    };
  }, [debouncedCalculate]);

  // Recalculate dimensions during expand/collapse transition
  useEffect(() => {
    const timers = [50, 100, 150, 200, 250, 320].map((ms) =>
      setTimeout(calculateDimensions, ms),
    );
    return () => timers.forEach(clearTimeout);
  }, [isExpanded]);

  const isEmpty = !presentationId;

  if (isEmpty && hasItems) {
    return (
      <div className="flex flex-1 bg-[#343437] rounded-2xl">
        <div className="flex flex-1 flex-col justify-center items-center gap-4">
          <img
            src={previewContentIcon}
            alt="Preview content"
            className="w-[54px] h-[57px]"
          />
          <Text variant="subheader" as="h3" className="text-gray-200">
            Preview Content
          </Text>
          <Text variant="body" as="p" className="text-gray-100">
            Select content from the left to show a preview
          </Text>
        </div>
      </div>
    );
  }

  if (isEmpty) {
    return (
      <div className="hidden lg:flex flex-1 bg-white rounded-2xl">
        <div className="flex flex-1 justify-center items-center bg-teal-50 rounded-2xl p-8 m-1">
          <div className="max-w-[446px] px-12 py-6 flex flex-1 flex-col justify-center gap-12 rounded-2xl">
            {STEPS.map((step, index) => (
              <div key={index} className="flex items-start gap-4 text-black">
                <img
                  src={step.icon}
                  alt=""
                  className="w-[50px] h-[50px] flex-shrink-0"
                />
                <div>
                  <Text variant="body" as="h3" className="font-bold mb-2">
                    {step.title}
                  </Text>
                  <Text variant="body" as="p">
                    {step.description}
                  </Text>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const isLandscape = true;
  const contentWidth = isLandscape ? PREVIEW_WIDTH : PREVIEW_HEIGHT;
  const contentHeight = isLandscape ? PREVIEW_HEIGHT : PREVIEW_WIDTH;
  const scaleX = dimensions.width / contentWidth;
  const scaleY = dimensions.height / contentHeight;
  const scale = Math.min(scaleX, scaleY);
  const marginTop = -(contentHeight / 2) * scale;
  const marginLeft = -(contentWidth / 2) * scale;

  // Render preview content
  const renderPreviewContent = () => {
    // Workaround: The generic preview error comes from the iframe (app-renderer),
    // which we can't customize. When preview fails due to upload issues,
    // we skip the iframe and render our own error message.
    if (presentation && uploadStatus.hasError) {
      return (
        <div
          className="h-full w-full min-h-0"
          style={{
            boxSizing: 'border-box',
            padding: ERROR_CONTAINER_PADDING,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'flex-end',
            backgroundColor: 'rgb(34, 32, 43)',
          }}
        >
          <div
            style={{
              fontFamily: 'sans-serif',
              fontWeight: 'bold',
              fontSize: ERROR_TITLE_FONT_SIZE,
              color: 'rgb(214, 214, 217)',
              letterSpacing: ERROR_TITLE_LETTER_SPACING,
              lineHeight: ERROR_TITLE_LINE_HEIGHT,
              marginTop: 0,
              marginBottom: ERROR_TITLE_MARGIN_BOTTOM,
            }}
          >
            Sorry, unable to display this content.
          </div>
          <div
            style={{
              fontFamily: 'Roboto, sans-serif',
              fontSize: ERROR_BODY_FONT_SIZE,
              color: 'rgb(152, 151, 160)',
              letterSpacing: 0,
              lineHeight: ERROR_BODY_LINE_HEIGHT,
              marginTop: 0,
              marginBottom: 0,
            }}
          >
            This content failed to upload and can&apos;t be displayed.
          </div>
        </div>
      );
    }
    if (presentation && appVersion) {
      return (
        <PresentationLoader
          presentation={presentation}
          presentationUploads={presentationUploads}
          appVersion={appVersion}
          theme={theme}
        />
      );
    }
    return null;
  };

  return (
    <div className="flex flex-1 bg-[#343437] rounded-2xl overflow-hidden relative">
      {/* Expand / Compress toggle button */}
      {onToggleExpand && (
        <Button
          variant="unstyled"
          className="absolute top-4 right-4 z-10 w-12 h-12 flex items-center justify-center bg-white rounded-2xl shadow hover:bg-gray-200 transition-colors"
          aria-label={isExpanded ? 'Collapse preview' : 'Expand preview'}
          onPress={onToggleExpand}
        >
          {isExpanded ? <CompressWide size={16} /> : <Expand size={14} />}
        </Button>
      )}

      {/* Preview container - measures available space */}
      <div ref={containerRef} className="relative flex-1 overflow-hidden">
        {/* Scaled preview content */}
        <div
          className="absolute top-1/2 left-1/2 bg-black shadow overflow-hidden"
          style={{
            width: contentWidth,
            height: contentHeight,
            marginTop,
            marginLeft,
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
          }}
        >
          {renderPreviewContent()}
        </div>
      </div>
    </div>
  );
};

export default PlaylistPreviewV2;
